<script setup>
import { HrFab } from '../primitives';

/**
 * The page's primary action, floating — the compact-width form of HrPageActionBtn.
 *
 * Extended rather than icon-only. An icon alone asks the reader to guess what a screen's one
 * primary action does. The label retracts once the reader starts scrolling, which is HrFab's doing
 * and is why it can afford to be there at all.
 *
 * **Sibling paths open as a menu, not a second target.** M3 has no split FAB, and a chevron beside
 * a label is two fingertip-sized targets in a control meant to be hit without looking. When there
 * is more than one route, pressing it opens the list with the primary route leading.
 *
 * It floats over the records rather than sitting above them because a phone has no room to spend on
 * a header that scrolls away. Anchor it with HrPageSticky, which has to be inside a page: this
 * draws the control, not its place, and has no way to check where it was put.
 *
 * @status recommended
 * @group actions
 * @useWhen The screen's primary action needs to stay reachable at a phone width
 * @avoidWhen The action is one of several of equal weight — a floating control claims to be the one
 * @related HrPageActionBtn
 * @a11y Carries its label as text, so it needs no aria-label of its own.
 */
defineOptions({ name: 'HrPageActionFab' });

/*
 * MD2's own floating action button, which Quasar draws for us — the colour is all it is missing.
 * @todo Remove when the legacy theme is removed.
 */
const LEGACY_PROPS = { color: 'primary', textColor: 'white' };

defineProps({
  /** What the action does, in the reader's terms. */
  label: {
    type: String,
    default: null,
  },
  /** Material icon name. */
  icon: {
    type: String,
    default: 'add',
  },
  /** Prevents interaction and drops the elevation, since nothing is raised to be pressed. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the user activates the control. */
  click: null,
});
</script>

<template>
  <hr-fab
    variant="primary"
    :legacy-props="LEGACY_PROPS"
    :label="label"
    :icon="icon"
    :disable="disable"
    @click="emit('click', $event)"
  >
    <!-- @slot The menu this opens when there is more than one route to the same outcome. -->
    <slot />
  </hr-fab>
</template>
